import { Builder, withChildren } from '@builder.io/react';
import React from 'react';
import { twMerge } from 'tailwind-merge';

import { targetAttributeOptions } from '../../constants';
import {
  Button as ButtonComponent,
  ButtonProps,
} from '../../storyblocks-design-system/components/atoms/Button/Button';

interface ButtonBuilderProps extends ButtonProps {
  link?: string;
  forAIPurpose?: boolean;
}

// Tailwind classes for button with AI purpose  ( AI Voiceover )
const AI_CLASSES = twMerge(
  'text-gray-900 rounded-xl',
  // Gradient border effect using background
  'p-0.5 bg-gradient-to-r from-purple-600 via-pink-200 to-pink-400'
);

const sparkleIcon = (
  <img
    src={`${__ASSETS_COMMON_IMAGES_URL__}/icons/ai-voiceover-diamond.svg`}
    alt="voiceover icon"
    className="w-4 h-4"
  />
);

export const Button = withChildren(function ({
  children,
  variant,
  className,
  size,
  disabled,
  link,
  target,
  forAIPurpose = false,
  ...rest
}: ButtonBuilderProps) {
  return (
    <ButtonComponent
      {...rest}
      variant={variant}
      className={forAIPurpose ? twMerge(AI_CLASSES, className) : className}
      anchorClassName={
        forAIPurpose &&
        'w-max text-gray-900 bg-bubblegum-100 hover:bg-transparent rounded-xl p-2 gap-x-2 inline-flex items-center hover:no-underline focus:no-underline cursor-pointer'
      }
      size={size}
      disabled={disabled}
      href={link}
      target={target}
      iconPrefix={forAIPurpose ? sparkleIcon : null}
    >
      {children}
    </ButtonComponent>
  );
});

Builder.registerComponent(Button, {
  name: 'Button SB',
  inputs: [
    {
      name: 'forAIPurpose',
      type: 'boolean',
      defaultValue: false,
      friendlyName: 'For AI Purpose',
    },
    {
      name: 'variant',
      type: 'string',
      defaultValue: 'primary',
      enum: [
        {
          label: 'primary',
          value: 'primary',
        },
        {
          label: 'secondary',
          value: 'secondary',
        },
        {
          label: 'outlined-dark',
          value: 'outlined-dark',
        },
        {
          label: 'outlined-light',
          value: 'outlined-light',
        },
        {
          label: 'text',
          value: 'text',
        },
        {
          label: 'alternate',
          value: 'alternate',
        },
      ],
    },
    {
      name: 'size',
      type: 'string',
      defaultValue: 'default',
      enum: [
        {
          label: 'default',
          value: 'default',
        },
        {
          label: 'large',
          value: 'large',
        },
      ],
    },
    {
      name: 'type',
      type: 'string',
      defaultValue: 'button',
      enum: [
        {
          label: 'button',
          value: 'button',
        },
        {
          label: 'submit',
          value: 'submit',
        },
      ],
    },
    {
      name: 'disabled',
      type: 'boolean',
      defaultValue: false,
      enum: [
        {
          label: 'Enabled',
          value: false,
        },
        {
          label: 'Disabled',
          value: true,
        },
      ],
    },
    {
      name: 'className',
      type: 'string',
      defaultValue: '',
    },
    {
      name: 'link',
      type: 'string',
      defaultValue: '',
    },
    {
      name: 'target',
      type: 'string',
      enum: targetAttributeOptions,
      defaultValue: '_blank',
      helperText:
        'https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-target',
      showIf: (options) => options.get('link'),
    },
  ],
  defaultChildren: [
    {
      '@type': '@builder.io/sdk:Element',
      component: { name: 'Text', options: { text: 'Text goes here.' } },
    },
  ],
});
